import { useEffect, useRef, useState } from "react";

/**
 * SectionReveal — wraps children with a fade-up + stagger entrance.
 * Triggers once on viewport entry. Respects prefers-reduced-motion.
 */
export default function SectionReveal({ as: Tag = "div", children, className = "", delay = 0, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Keep content readable when motion is unwanted or observation unavailable.
    if (reduced || typeof window.IntersectionObserver !== "function") return;
    let io;
    try {
      io = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisible(true);
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -4% 0px", threshold: 0.08 }
      );
      io.observe(el);
    } catch {
      io?.disconnect();
      return;
    }
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`lf-reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ animationDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
